/**
 * /bookingflight — Flight Search (Phase 4).
 *
 * Sastaticket-style search form: trip type, travellers, cabin class, airport
 * autocomplete (Flying From / To), departure/return dates, and a direct-only toggle.
 * On submit it writes the search criteria to BookingContext and navigates to the
 * results page.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAirports } from "../../mock/api";
import { useBooking } from "../../context/BookingContext";
import AirportAutocomplete from "../../components/booking/AirportAutocomplete";
import TravellersSelector from "../../components/booking/TravellersSelector";
import CabinClassSelector from "../../components/booking/CabinClassSelector";
import "./flight-search.css";

const TRIP_TYPES = [
  { key: "oneway", label: "One Way", icon: "fas fa-arrow-right" },
  { key: "return", label: "Return", icon: "fas fa-exchange-alt" },
  { key: "multicity", label: "Multi-city", icon: "fas fa-route" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const sanitizeISODateInput = (value) => value.replace(/[^\d-]/g, "").slice(0, 10);

const isValidISODate = (value) => {
  if (!ISO_DATE_PATTERN.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
};

const emptySegment = () => ({ from: null, to: null, date: todayISO() });

export default function FlightSearch() {
  const navigate = useNavigate();
  const { searchCriteria, setSearchCriteria } = useBooking();

  const [tripType, setTripType] = useState(searchCriteria?.tripType || "oneway");
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [departureDate, setDepartureDate] = useState(searchCriteria?.departureDate || todayISO());
  const [returnDate, setReturnDate] = useState(searchCriteria?.returnDate || "");
  const [travellers, setTravellers] = useState({
    adults: searchCriteria?.adults || 1,
    children: searchCriteria?.children || 0,
    infants: searchCriteria?.infants || 0,
  });
  const [cabin, setCabin] = useState(searchCriteria?.cabin || "Economy");
  const [directOnly, setDirectOnly] = useState(searchCriteria?.directOnly || false);
  const [segments, setSegments] = useState([emptySegment(), emptySegment()]);

  // Hydrate pre-filled from/to codes (e.g. arriving from a "Book Now" deal) into
  // full airport objects the autocomplete can display.
  useEffect(() => {
    if (!searchCriteria?.from && !searchCriteria?.to) return;
    let active = true;
    getAirports().then((list) => {
      if (!active) return;
      const byCode = Object.fromEntries(list.map((a) => [a.airportCode, a]));
      if (searchCriteria.from && byCode[searchCriteria.from]) setFrom(byCode[searchCriteria.from]);
      if (searchCriteria.to && byCode[searchCriteria.to]) setTo(byCode[searchCriteria.to]);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const updateSegment = (index, patch) =>
    setSegments((segs) => segs.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const addSegment = () =>
    setSegments((segs) => (segs.length >= 5 ? segs : [...segs, emptySegment()]));

  const removeSegment = (index) =>
    setSegments((segs) => (segs.length <= 2 ? segs : segs.filter((_, i) => i !== index)));

  const totalTravellers = travellers.adults + travellers.children + travellers.infants;

  const isMulti = tripType === "multicity";

  const validate = () => {
    if (isMulti) {
      for (let i = 0; i < segments.length; i++) {
        const s = segments[i];
        if (!s.from || !s.to) {
          toast.error(`Please choose origin and destination for flight ${i + 1}.`);
          return false;
        }
        if (s.from.airportCode === s.to.airportCode) {
          toast.error(`Flight ${i + 1}: origin and destination can't match.`);
          return false;
        }
        if (!s.date) {
          toast.error(`Please pick a date for flight ${i + 1}.`);
          return false;
        }
        if (!isValidISODate(s.date)) {
          toast.error(`Flight ${i + 1}: enter the date as YYYY-MM-DD.`);
          return false;
        }
        if (s.date < todayISO()) {
          toast.error(`Flight ${i + 1}: departure date cannot be in the past.`);
          return false;
        }
      }
      return true;
    }

    if (!from || !to) {
      toast.error("Please choose both origin and destination.");
      return false;
    }
    if (from.airportCode === to.airportCode) {
      toast.error("Origin and destination can't be the same.");
      return false;
    }
    if (!departureDate) {
      toast.error("Please pick a departure date.");
      return false;
    }
    if (!isValidISODate(departureDate)) {
      toast.error("Enter the departure date as YYYY-MM-DD.");
      return false;
    }
    if (departureDate < todayISO()) {
      toast.error("Departure date cannot be in the past.");
      return false;
    }
    if (tripType === "return" && !returnDate) {
      toast.error("Please pick a return date.");
      return false;
    }
    if (tripType === "return" && !isValidISODate(returnDate)) {
      toast.error("Enter the return date as YYYY-MM-DD.");
      return false;
    }
    if (tripType === "return" && returnDate < departureDate) {
      toast.error("Return date must be after departure.");
      return false;
    }
    return true;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const base = {
      tripType,
      travellers: totalTravellers,
      adults: travellers.adults,
      children: travellers.children,
      infants: travellers.infants,
      cabin,
      directOnly,
    };

    let criteria;
    if (isMulti) {
      const legs = segments.map((s) => ({
        from: s.from.airportCode,
        to: s.to.airportCode,
        departureDate: s.date,
      }));
      // Results page renders the first leg (mock generator is single-route); all legs stored.
      criteria = {
        ...base,
        from: legs[0].from,
        to: legs[0].to,
        departureDate: legs[0].departureDate,
        segments: legs,
      };
    } else {
      criteria = {
        ...base,
        from: from.airportCode,
        to: to.airportCode,
        departureDate,
        returnDate: tripType === "return" ? returnDate : undefined,
      };
    }

    setSearchCriteria(criteria);
    navigate("/bookingflight/results");
  };

  const heading = useMemo(() => {
    switch (tripType) {
      case "return":
        return "Search return flights";
      case "multicity":
        return "Plan a multi-city trip";
      default:
        return "Search one-way flights";
    }
  }, [tripType]);

  return (
    <section className="fs-page">
      <div className="fs-hero">
        <div className="fs-hero__inner">
          <h1 className="fs-hero__title">Book Your Flight</h1>
          <p className="fs-hero__subtitle">Compare fares across top airlines and book in minutes.</p>
        </div>
      </div>

      <form className="fs-panel" onSubmit={handleSearch}>
        <h2 className="fs-panel__heading">{heading}</h2>

        {/* Trip type */}
        <div className="fs-trip-types">
          {TRIP_TYPES.map((t) => (
            <button
              type="button"
              key={t.key}
              className={`fs-trip ${tripType === t.key ? "fs-trip--active" : ""}`}
              onClick={() => setTripType(t.key)}
            >
              <i className={t.icon} aria-hidden="true" /> {t.label}
            </button>
          ))}
        </div>

        {/* Route + dates */}
        {!isMulti && (
          <div className="fs-route-grid">
            <div className="fs-route-fields">
              <AirportAutocomplete
                label="Flying From"
                value={from}
                onChange={setFrom}
                placeholder="City or airport"
                icon="fas fa-plane-departure"
                excludeCode={to?.airportCode}
              />
              <button
                type="button"
                className="fs-swap"
                onClick={swap}
                aria-label="Swap origin and destination"
                title="Swap"
              >
                <i className="fas fa-exchange-alt" aria-hidden="true" />
              </button>
              <AirportAutocomplete
                label="Flying To"
                value={to}
                onChange={setTo}
                placeholder="City or airport"
                icon="fas fa-plane-arrival"
                excludeCode={from?.airportCode}
              />
            </div>

            <div className="fs-date-fields">
              <div className="fs-field">
                <label className="fs-field__label">Departure Date</label>
                <div className="fs-date">
                  <i className="fas fa-calendar-alt" aria-hidden="true" />
                  <input
                    type="date"
                    value={departureDate}
                    min={todayISO()}
                    autoComplete="off"
                    onChange={(e) => setDepartureDate(e.target.value)}
                  />
                </div>
              </div>

              {tripType === "return" && (
                <div className="fs-field">
                  <label className="fs-field__label">Return Date</label>
                  <div className="fs-date">
                    <i className="fas fa-calendar-alt" aria-hidden="true" />
                    <input
                      type="date"
                      value={returnDate}
                      min={departureDate || todayISO()}
                      autoComplete="off"
                      onChange={(e) => setReturnDate(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Multi-city segments */}
        {isMulti && (
          <div className="fs-segments">
            {segments.map((seg, i) => (
              <div className="fs-segment" key={i}>
                <span className="fs-segment__badge">Flight {i + 1}</span>
                <div className="fs-segment__fields">
                  <AirportAutocomplete
                    label="From"
                    value={seg.from}
                    onChange={(a) => updateSegment(i, { from: a })}
                    icon="fas fa-plane-departure"
                    excludeCode={seg.to?.airportCode}
                  />
                  <AirportAutocomplete
                    label="To"
                    value={seg.to}
                    onChange={(a) => updateSegment(i, { to: a })}
                    icon="fas fa-plane-arrival"
                    excludeCode={seg.from?.airportCode}
                  />
                  <div className="fs-field">
                    <label className="fs-field__label">Date</label>
                    <div className="fs-date">
                      <i className="fas fa-calendar-alt" aria-hidden="true" />
                      <input
                        type="date"
                        value={seg.date}
                        min={todayISO()}
                        autoComplete="off"
                        onChange={(e) =>
                          updateSegment(i, { date: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
                {segments.length > 2 && (
                  <button
                    type="button"
                    className="fs-segment__remove"
                    onClick={() => removeSegment(i)}
                    aria-label={`Remove flight ${i + 1}`}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
            {segments.length < 5 && (
              <button type="button" className="fs-add-segment" onClick={addSegment}>
                <i className="fas fa-plus" aria-hidden="true" /> Add another flight
              </button>
            )}
          </div>
        )}

        {/* Travellers + cabin */}
        <div className="fs-options-grid">
          <TravellersSelector value={travellers} onChange={setTravellers} />
          <CabinClassSelector value={cabin} onChange={setCabin} />
        </div>

        {/* Direct + submit */}
        <div className="fs-footer">
          <label className="fs-direct">
            <input
              type="checkbox"
              checked={directOnly}
              onChange={(e) => setDirectOnly(e.target.checked)}
            />
            <span>Search direct flights only</span>
          </label>

          <button type="submit" className="fs-search-btn">
            <i className="fas fa-search" aria-hidden="true" /> Search Flights
          </button>
        </div>
      </form>
    </section>
  );
}
